import React, { useState, useContext, useEffect, useMemo } from 'react'

import { Drawer, notification } from 'antd'
import { requestWithPromise, generalErrorHandler, isObject } from '../../utils'
import UserSectorTable, { DATA_TYPE_SELECT, DATA_TYPE_CHECK_LOCAL_ADMINISTRATOR, DATA_TYPE_CHECK_CONTACT_PERSON, DATA_TYPE_TAG } from './UserSectorTable'
import { faTrash } from '@fortawesome/pro-solid-svg-icons'
import { OrderBy, Contexts } from '../../utils/constants'
import { getUser, setUserManagedSectors } from '../../reducers/UserReducer'
import { connect } from 'react-redux'
import { getTranslate } from 'react-localize-redux'
import { HttpMethods, onError, onSuccess } from '../../utils/apiHelper'
import { bindActionCreators } from 'redux'
import Loading from '../../HOC/Loading'
import { getInstitutionSectors } from '../../utils/api/institution'
import { getUserSectors } from '../../utils/api/user'
import { InstitutionContext } from '../../Providers/InstitutionProvider'
import { isHospitalAdmin, isInstitutionGroupAdmin, isNurse } from '../../utils/roles'
import { addContactPerson, addContactPersonToMultipleSectors, addManagedUser, addManagedUserToMultipleSectors } from '../../utils/api/sector'

const mapStateToProps = state => ({ user: getUser(state.getUser), t: getTranslate(state.locale) })

const mapDispatchToProps = dispatch => ({ setReduxUserManagedSectors: bindActionCreators(setUserManagedSectors, dispatch) })

const UserManagedSectors = ({ user, schoolId, selectedUser, onClose, setReduxUserManagedSectors, t }) => {
  const ic = useContext(InstitutionContext)

  const [userAffiliatedSectors, setUserAffiliatedSectors] = useState([])
  const [userSectors, setUserSectors] = useState({ contactPersons: [], affiliated: [] })

  const [loading, setLoading] = useState(false)

  const sectors = useMemo(() => (isObject(ic) ? ic.sectors : []), [ic])
  const archivedSectors = useMemo(() => (isObject(ic) ? ic.archivedSectors : []), [ic])

  const isSuperAdmin = useMemo(() => user.context === Contexts.ADMIN, [user])
  const affiliatedSectors = useMemo(() => {
    return userAffiliatedSectors.map(s => {
      return {
        id: s.id,
        sector: s.id,
        name: s.name,
        archived: s.archived
      }
    }).filter(s => !s.archived || archivedSectors.some(si => si.id === s.id))
  }, [userAffiliatedSectors])

  useEffect(() => {
    if (selectedUser !== null) {
      const request = { call: getUserSectors, parameters: [user, selectedUser] }

      if (isHospitalAdmin(selectedUser) || isInstitutionGroupAdmin(selectedUser)) {
        request.call = getInstitutionSectors
        request.parameters = [user, selectedUser.institutions[0], { orderBy: OrderBy.NAME, strictMode: 0 }]
      }

      setLoading(true)

      request.call(...request.parameters).then(json => {
        if (json?.data) {
          loadUser(json.data)
        }

        setLoading(false)
      })
    } else {
      setUserAffiliatedSectors([])
    }
  }, [selectedUser, user])

  const loadUser = sectors => {
    const userSectors = {
      contactPersons: [],
      affiliated: []
    }

    sectors.forEach(s => {
      userSectors.contactPersons[s.id] = s.contactPersons.map(contactPerson => {
        return contactPerson.id
      })
      userSectors.affiliated[s.id] = s.affiliates.map(user => {
        return user.id
      })
    })

    setUserSectors(userSectors)
    setUserAffiliatedSectors(sectors)
  }

  const handleSectorAddition = sector => {
    if (sector.newId === null) {
      return
    }

    const newSector = sectors.find(s => s.id === sector.newId)

    let affiliatedSectors = userAffiliatedSectors
    const userManagedSectorIds = affiliatedSectors.map(s => s.id)

    if (userManagedSectorIds.includes(newSector.id)) {
      onError(t('This user already coordinates this sector'))
      return
    } else {
      affiliatedSectors = userAffiliatedSectors.concat(newSector)
    }

    handleUserCoordinatedSectorUpdating(affiliatedSectors)
  }

  const handleSectorEdition = sector => {
    const editedSector = sectors.find(s => s.id === sector.newId)
    const userManagedSectorIds = affiliatedSectors.map(s => s.id)

    if (userManagedSectorIds.includes(editedSector.id)) {
      onError(t('This user already coordinates this sector'))

      return
    }

    handleUserCoordinatedSectorUpdating(userAffiliatedSectors.map(ums => ums.id === sector.prevId ? editedSector : ums))
  }

  const handleSectorDeletion = sector => {
    const affiliatedSectors = userAffiliatedSectors.filter(ums => ums.id !== sector.sector)

    handleUserCoordinatedSectorUpdating(affiliatedSectors, sector.sector)
  }

  const handleUserCoordinatedSectorUpdating = (userAffiliatedSectors, sectorId = null, confirmationMessage = false) => {
    if (selectedUser.id === user.id) {
      setReduxUserManagedSectors(userAffiliatedSectors)
    }

    setUserAffiliatedSectors(userAffiliatedSectors)

    const body = { affiliatedSectors: userAffiliatedSectors.map(ums => ums.id) }

    if (isSuperAdmin && schoolId) {
      body.schoolId = schoolId
    }

    if (sectorId && isNurse(selectedUser) && userIsContactPerson(sectorId)) {
      body.sectorToRemoveContactPerson = sectorId
    }

    requestWithPromise('/user/modify/' + selectedUser.id, HttpMethods.POST, body, user)
      .then((json) => {
        if (confirmationMessage) {
          notification.success({ message: t('Saved changes'), placement: 'bottomRight' })
        }
      })
      .catch((error) => { generalErrorHandler(error) })
  }

  const userIsContactPerson = sectorId => {
    if (selectedUser && userSectors.contactPersons[sectorId]) {
      return userSectors.contactPersons[sectorId].includes(selectedUser.id)
    }
  }

  const userIsAdmin = sectorId => {
    if (selectedUser && userSectors.affiliated[sectorId]) {
      return userSectors.affiliated[sectorId].includes(selectedUser.id)
    }
  }

  const setUserContactPerson = sectorId => {
    addContactPerson(user, { id: sectorId }, { userToAdd: selectedUser.id }).then(() => onSuccess(t('Saved changes'), 'bottomLeft'))
  }

  const setManagedUser = sectorId => {
    addManagedUser(user, { id: sectorId }, { userToAdd: selectedUser.id }).then(() => onSuccess(t('Saved changes'), 'bottomLeft'))
  }

  const setMultipleUserContactPersons = sectors => {
    addContactPersonToMultipleSectors(user, { userToAdd: selectedUser.id, sectors: sectors }).then(() => onSuccess(t('Saved changes'), 'bottomLeft'))
  }

  const setMultipleManagedUsers = sectors => {
    addManagedUserToMultipleSectors(user, { userToAdd: selectedUser.id, sectors: sectors }).then(() => onSuccess(t('Saved changes'), 'bottomLeft'))
  }

  const searchWithFilter = (value, isAdmin) => {
    if (isAdmin) {
      getInstitutionSectors(user, selectedUser.institutions[0], { search: value, orderBy: OrderBy.NAME, strictMode: 0 }).then(json => {
        if (json?.data) {
          loadUser(json.data)
        }
      })
    } else {
      getUserSectors(user, selectedUser, { search: value }).then(json => {
        if (json?.data) {
          loadUser(json.data)
        }
      })
    }
  }

  const columns = useMemo(() => {
    const columns = [{ type: DATA_TYPE_SELECT, name: t('common.sector'), options: sectors }]

    if (isHospitalAdmin(selectedUser)) {
      columns.push({ type: DATA_TYPE_CHECK_LOCAL_ADMINISTRATOR, name: t('Local administrator') })
    }

    columns.push({ type: DATA_TYPE_CHECK_CONTACT_PERSON, name: t('common.contact_persons') })
    columns.push({ type: DATA_TYPE_TAG, name: t('Status') })

    return columns
  }, [t, sectors, selectedUser])

  const additionalActions = [
    {
      icon: faTrash,
      type: 'danger',
      title: 'Delete',
      titlePopconfirm: t('Delete this sector?'),
      handleOnClick: handleSectorDeletion
    }
  ]

  return (
    <Drawer
      title={isObject(selectedUser) ? t('Sectors managed by') + ' ' + selectedUser.email : '?'}
      width='640px'
      onClose={onClose}
      visible={selectedUser !== null}
    >
      <Loading loading={loading}>
        <UserSectorTable
          onDataAdd={handleSectorAddition}
          onDataEdit={handleSectorEdition}
          data={affiliatedSectors}
          columns={columns}
          additionalActions={additionalActions}
          userIsContactPerson={userIsContactPerson}
          userIsAdmin={userIsAdmin}
          setUserContactPerson={setUserContactPerson}
          setManagedUser={setManagedUser}
          setMultipleUserContactPersons={setMultipleUserContactPersons}
          setMultipleManagedUsers={setMultipleManagedUsers}
          user={selectedUser}
          searchWithFilter={searchWithFilter}
        />
      </Loading>
    </Drawer>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(UserManagedSectors)
